import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Button, Drawer, Field, Input, Select } from '@itds/elements/index.js';
import {
  PRODUCT_TYPE_DETAILS_BY_PRODUCT_TYPE,
  FORMULA_LABELS,
  DEFAULT_FORMULAS,
  FORMULA_VARIABLES
} from '@itds/Constants';
import { FontSizes, FontWeights, Colors, BorderRadius } from '@itds/Theme';

import Api from '@carrier/utils/Api';

const Edit = ({ formula, onClose, onSave, show }) => {
  const styles = {
    variables: {
      background: '#f5f5f5',
      padding: 20,
      border: '1px solid #ccc',
      borderRadius: `${BorderRadius.DEFAULT}px ${BorderRadius.DEFAULT}px 0 0`,
      marginBottom: -4,
      display: 'flex'
    },
    variable: {
      fontSize: FontSizes.REGULAR,
      fontWeight: FontWeights.BOLD,
      background: Colors.GREEN.hex,
      color: '#fff',
      borderRadius: BorderRadius.DEFAULT,
      padding: '6px 10px',
      margin: '8px 4px auto 4px'
    }
  };
  const [data, setData] = useState({
    id: null,
    carrier_id: null,
    data_version_id: null,
    product_type: 'Term',
    product_id: '',
    formula_type: 'base_premium',
    formula: DEFAULT_FORMULAS.base_premium
  });
  const [errors, setErrors] = useState([]);
  const [save_error, setSaveError] = useState(false);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    const new_formula = formula || {};

    setData({
      id: new_formula.id,
      carrier_id: new_formula.carrier_id,
      data_version_id: new_formula.data_version_id,
      product_type: new_formula.product_type || 'Term',
      product_id: new_formula.product_id || '',
      formula_type: new_formula.formula_type || 'base_premium',
      formula: new_formula.formula || DEFAULT_FORMULAS.base_premium
    });

    if (new_formula.carrier_id) {
      Api.loadProducts({ carrier_id: new_formula.carrier_id }).then(({ data }) => {
        setProducts(data);
      });
    }

    setErrors([]);
  }, [formula]);

  const handleInputChange = (name, e) => {
    const new_state = { [name]: e.target.value };

    if (name === 'formula_type') {
      new_state.formula = DEFAULT_FORMULAS[e.target.value];
    }

    setData({ ...data, ...new_state });
  };

  const handleSave = () => {
    const errors = [];

    if (!data.formula) {
      errors.push('formula');
    }

    setErrors(errors);

    if (!errors.length) {
      const { id, ...payload } = data;

      Api.saveFormula(id, payload)
        .then(() => {
          onSave();
          setSaveError(false);
        })
        .catch(() => {
          setSaveError(true);
          setTimeout(() => {
            setSaveError(false);
          }, 2000);
        });
    }
  };

  return (
    <Drawer
      alignRight={true}
      footer={<Button onClick={handleSave}>{data.id ? 'Save Changes' : 'Create Formula'}</Button>}
      onClose={onClose}
      show={show}
      title={data.id ? 'Editing Formula' : 'New Formula'}
      wide={true}
    >
      {save_error ? (
        <Alert type='error'>An error occurred while trying to save the formula.</Alert>
      ) : null}

      {/* hide these fields if editing formula, display if creating new formula */}
      {data.id ? null : (
        <>
          <Field label='Product Type'>
            <Select
              invalid={errors.includes('product_type')}
              onChange={handleInputChange.bind(null, 'product_type')}
              options={Object.keys(PRODUCT_TYPE_DETAILS_BY_PRODUCT_TYPE).map(k => ({ value: k }))}
              value={data.product_type}
            />
          </Field>

          <Field label='Product'>
            <Select
              invalid={errors.includes('product_id')}
              onChange={handleInputChange.bind(null, 'product_id')}
              options={[{ value: '', label: 'All Products' }].concat(
                products
                  .filter(p => p.product_type === data.product_type)
                  .map(p => ({ value: p.id, label: p.product_name }))
              )}
              value={data.product_id}
            />
          </Field>

          <Field label='Type'>
            <Select
              invalid={errors.includes('formula_type')}
              onChange={handleInputChange.bind(null, 'formula_type')}
              options={Object.keys(FORMULA_LABELS).map(key => ({
                value: key,
                label: FORMULA_LABELS[key]
              }))}
              value={data.formula_type}
            />
          </Field>
        </>
      )}
      <Field label='Formula'>
        <div style={styles.variables}>
          <div style={{ display: 'flex', flexWrap: 'wrap', width: '50%' }}>
            <div style={{ ...styles.variable }}>+</div>
            <div style={{ ...styles.variable }}>-</div>
            <div style={{ ...styles.variable }}>*</div>
            <div style={{ ...styles.variable }}>/</div>
            <div style={{ ...styles.variable }}>round()</div>
            <div style={{ ...styles.variable }}>min()</div>
            <div style={{ ...styles.variable }}>max()</div>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', width: '50%' }}>
            {FORMULA_VARIABLES[data.formula_type].map((v, i) => {
              return (
                <div key={i} style={{ ...styles.variable, background: Colors.BLUE.hex }}>
                  {v}
                </div>
              );
            })}
          </div>
        </div>
        <Input
          height={100}
          invalid={errors.includes('formula')}
          onChange={handleInputChange.bind(null, 'formula')}
          required={true}
          type='textarea'
          value={data.formula}
        />
      </Field>
    </Drawer>
  );
};

Edit.propTypes = {
  formula: PropTypes.object,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  show: PropTypes.bool
};

export default Edit;
